import { createSlice } from "@reduxjs/toolkit";
import { FirestoreService } from "../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../api/firebase/references";
import { ToastMessages } from "../../components/toasts/toastMessages";
import { NccnIcdConfig } from "../../data-model/nccnIcdConfig";

export interface NccnIcdConfigSliceModel {
  data: NccnIcdConfig[];
  loading: boolean;
  error: string | null;
}

const initialState: NccnIcdConfigSliceModel = {
  data: [],
  loading: false,
  error: null,
};

const nccnIcdConfigSlice = createSlice({
  name: "nccnIcdConfig",
  initialState,
  reducers: {
    fetchingNccnIcdConfig: (state) => {
      state.loading = true;
      state.error = null;
      state.data = [];
    },
    setNccnIcdConfig: (state, action) => {
      state.data = action.payload;
      state.loading = false;
      state.error = null;
    },
    errorFetchingNccnIcdConfig: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    resetNccnIcdConfig: (state) => {
      state.data = [];
      state.loading = false;
      state.error = null;
    },
  },
});

export const {
  fetchingNccnIcdConfig,
  setNccnIcdConfig,
  errorFetchingNccnIcdConfig,
  resetNccnIcdConfig,
} = nccnIcdConfigSlice.actions;

export const fetchNccnIcdConfigFromFirebase =
  (orgId: string) => async (dispatch) => {
    dispatch(fetchingNccnIcdConfig());
    try {
      const docs = await FirestoreService.getAllDocuments<NccnIcdConfig>(
        FirestoreCollectionReference.nccnIcdConfig(orgId),
      );

      dispatch(setNccnIcdConfig(docs));
    } catch (error) {
      dispatch(errorFetchingNccnIcdConfig((error as Error).message));
    }
  };

export const createNccnIcdConfig =
  (
    orgId: string,
    data: Omit<NccnIcdConfig, "id">,
    usePreviousConfigId?: string,
  ) =>
  async (dispatch, getState) => {
    try {
      let configData: Omit<NccnIcdConfig, "id"> = { ...data };

      // If using previous config, copy its data
      if (usePreviousConfigId) {
        const state = getState();
        const previousConfig = state.nccnIcdConfig.data.find(
          (config) => config.id === usePreviousConfigId,
        );
        if (!previousConfig) {
          throw new Error(ToastMessages.DX_CODE_CONFIG_PREVIOUS_NOT_FOUND);
        }
        configData = {
          ...configData,
          category: previousConfig.category || "",
          description: previousConfig.description || "",
          codes: previousConfig.codes || [],
          priority_config_org_specific:
            previousConfig.priority_config_org_specific,
          total_codes: previousConfig.total_codes || 0,
        };
      }

      // Use code as document ID, and calculate total_codes if not already set
      const finalData = {
        ...configData,
        created_date: configData.created_date || new Date().toISOString(),
        total_codes:
          configData.total_codes !== undefined
            ? configData.total_codes
            : configData.codes?.length || 0,
      };

      const newDoc = await FirestoreService.addDocumentWithId<NccnIcdConfig>(
        FirestoreCollectionReference.nccnIcdConfig(orgId),
        {
          id: data.code, // Use code as document ID
          ...finalData,
        } as any,
      );

      // Refresh the list
      dispatch(fetchNccnIcdConfigFromFirebase(orgId));
      return newDoc;
    } catch (error) {
      dispatch(errorFetchingNccnIcdConfig((error as Error).message));
      throw error;
    }
  };

export const updateNccnIcdConfig =
  (orgId: string, docId: string, updates: Partial<NccnIcdConfig>) =>
  async (dispatch) => {
    try {
      await FirestoreService.updateDocument(
        FirestoreCollectionReference.nccnIcdConfig(orgId),
        docId,
        updates,
      );

      // Refresh the list
      dispatch(fetchNccnIcdConfigFromFirebase(orgId));
    } catch (error) {
      dispatch(errorFetchingNccnIcdConfig((error as Error).message));
      throw error;
    }
  };

export default nccnIcdConfigSlice.reducer;
